import { useMemo } from 'react';
import type { ComponentProps } from 'react';
import { LoadingSpinner, Button, useTheme } from '@simplywallst/ui-core';

import Tooltip from '@/components/Tooltip';

import { MinimalButton, StarOutlineSVG, StarSolidSVG } from './styled';
import useAddNoteAfterWatchingStock from '@/pages/CompanyReport/hooks/useAddNoteAfterWatchingStock';

type Props = ComponentProps<typeof Button> & {
  minimal: boolean;
  isLoading: boolean;
  existsInWatchlist: boolean;
  companyId: string;
};

const AddRemoveCompanyButton = ({
  minimal,
  isLoading,
  existsInWatchlist,
  companyId,
  ...rest
}: Props) => {
  const isAddNotesAfterWatchingStockEnabled = useAddNoteAfterWatchingStock({
    companyId,
  });

  const theme = useTheme();
  const label = `${existsInWatchlist ? 'Added' : 'Add'} to watchlist`;

  const icon = useMemo(() => {
    if (isLoading) {
      return (
        <LoadingSpinner
          light
          top="50%"
          left="20px"
          width={16}
          zIndex={1}
          aria-busy="true"
        />
      );
    }

    if (existsInWatchlist) {
      return (
        <StarSolidSVG
          data-cy-id="solid-star"
          fill={theme.color.white}
          alt={minimal ? label : ''}
          role={minimal ? '' : 'presentation'}
        />
      );
    }

    return (
      <StarOutlineSVG
        fill={theme.color.white}
        data-cy-id="outline-star"
        opacity={minimal ? 0.5 : 1}
        alt={minimal ? label : ''}
        role={minimal ? '' : 'presentation'}
      />
    );
  }, [label, theme, minimal, isLoading, existsInWatchlist]);

  if (minimal) {
    return (
      <Tooltip title={label}>
        <MinimalButton {...rest}>{icon}</MinimalButton>
      </Tooltip>
    );
  }

  return (
    <Button
      alignIcon={!isAddNotesAfterWatchingStockEnabled ? 'left' : undefined}
      {...rest}
    >
      {icon}
      {!isAddNotesAfterWatchingStockEnabled && label}
    </Button>
  );
};

export default AddRemoveCompanyButton;
